import { ofetch } from 'ofetch';
import { readBrowserPage } from '@shared/browser/BrowserPage.browser.ts';
import { log } from '@shared/log.browser.ts';
import type { PrimitiveLabels } from '@shared/tracking/TrackingLabels.ts';
import type { Page } from '@shared/types/Tilelist.ts';
import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
import { createTileListPageLoader } from '@tilelist/application/core/TileListPageLoader.ts';
import type { TileListPageSession } from '@tilelist/application/core/TileListSession.ts';
import { createPageSession } from '@tilelist/application/core/TileListSession.ts';
import type { EventsIncoming } from '@tilelist/application/ports/EventsIncoming.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import type { TileListLoaded } from '@tilelist/application/ports/TileListLoaded.ts';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';
import { EvergladesClient } from '@tilelist/clients/EvergladesClient.ts';
import {
    type NavigationCheckpoint,
    createNavigationCheckpoint,
    resolveEntryRule,
} from '@tilelist/components/history/NavigationCheckpoint.ts';
import { ProductTileController } from '@tilelist/controllers/ProductTileController.browser.ts';
import { FullTileListStrategy } from '@tilelist/domain/composition/strategies/FullTileListStrategy.ts';
import type { PageLoadStrategy } from '@tilelist/domain/composition/strategies/PageLoadStrategy.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import { collectFeatureTracking } from '@tilelist/domain/tracking/ExperimentTracking.ts';
import { createCacheKeyRefresher } from '@tilelist/services/cache/CacheKeyRefresher.browser.ts';
import { restoreBySelector, saveScrollSelector } from '@tilelist/services/cache/ScrollPositionCache.browser.ts';
import { maybeRestoreFromCache, saveToCache } from '@tilelist/services/cache/TileListCache.browser.svelte.ts';
import { createEventsIncoming } from '@tilelist/services/events/EventsIncoming.browser.ts';
import { createEventsOutgoing } from '@tilelist/services/events/EventsOutgoing.browser.ts';
import { createTileListLoadedNexus } from '@tilelist/services/events/TileListLoaded.browser.ts';
import { getTrackingService, getVariationLoadingService } from '@tilelist/services/ServiceFactory.browser.ts';
import { createAppsTracking, trackAdjustForTileList } from '@tilelist/services/tracking/AppsTracking.browser.ts';
import { createEntryRuleTracking } from '@tilelist/services/tracking/EntryRuleTracking.browser.ts';
import { createEntryRuleTrackingService } from '@tilelist/services/tracking/EntryRuleTrackingService.browser.ts';
import { createFirebaseTracking, trackFirebase } from '@tilelist/services/tracking/FirebaseTracking.browser.ts';
import { getPageViewTracking } from '@tilelist/services/tracking/PageViewTracking.browser.ts';
import { createTrackingClientReloadContextProvider } from '@tilelist/services/TrackingClientReloadContextProvider.browser.ts';

export type StrategyFactory = (variationLoader: VariationLoader) => PageLoadStrategy;
export type BrowserHistoryStateReader = () => unknown;

function createEvergladesClient(): EvergladesClient {
    return new EvergladesClient({ fetchClient: ofetch, logger: log, timeout: 10000 });
}

export type TileListBrowserWiring = {
    session: TileListPageSession;
    eventsIncoming: EventsIncoming;
    eventsOutgoing: EventsOutgoing;
    trackingService: TrackingPort;
    variationLoadingService: VariationLoader;
    controller: ProductTileController;
    tileListLoaded: TileListLoaded;
    createNavigationCheckpoint: (rule: string, page: Page, tsLink?: PrimitiveLabels) => NavigationCheckpoint;
    restoreScrollPosition(): Promise<void>;
    saveScrollSelector(): void;
    savePageToCache(pageView: TileListPageLayout): Promise<void>;
    maybeRestoreFromCache(pageView: TileListPageLayout): Promise<TileListPageLayout>;
    sendTracking(pageView: TileListPageLayout, historic?: boolean): void;
    cleanup(): void;
};

export function createTileListBrowserWiring(
    initialSession: TileListInitialSession,
    strategyFactory?: StrategyFactory,
    readHistoryState: BrowserHistoryStateReader = () => history.state,
): TileListBrowserWiring {
    const trackingService = getTrackingService();
    const variationLoadingService = getVariationLoadingService();
    const tileListLoadedNexus = createTileListLoadedNexus();
    const eventsOutgoing = createEventsOutgoing(tileListLoadedNexus.signal);
    const eventsIncoming = createEventsIncoming();
    const firebaseTracking = createFirebaseTracking();
    const appsTracking = createAppsTracking();
    const entryRuleTracking = createEntryRuleTracking();
    const reloadContextProvider = createTrackingClientReloadContextProvider(trackingService);
    let entryRule = resolveEntryRule(readHistoryState(), initialSession.page.context.rule);
    const getEntryRule = () => entryRule;
    const evergladesClient = createEvergladesClient();
    const strategy = strategyFactory?.(variationLoadingService) ?? new FullTileListStrategy(variationLoadingService);
    const pageLoader = createTileListPageLoader({
        pageFetcher: evergladesClient,
        variationLoadingService,
        strategy,
    });
    const pageSession = createPageSession(initialSession, {
        pageLoader,
        reloadContextProvider,
        cacheKeyRefresher: createCacheKeyRefresher(),
    });
    const session: TileListPageSession = {
        ...pageSession,
        reload(event) {
            if (event.historic && event.entryRule !== undefined) entryRule = event.entryRule;
            return pageSession.reload(event, entryRule);
        },
    };
    const controller = new ProductTileController(variationLoadingService, trackingService, getEntryRule);
    const checkpointFactory: TileListBrowserWiring['createNavigationCheckpoint'] = (rule, page, tsLink) =>
        createNavigationCheckpoint(rule, getEntryRule(), page, tsLink);
    const disposeEntryRuleTracking = createEntryRuleTrackingService(
        session.onex,
        getEntryRule,
        tileListLoadedNexus.events,
        entryRuleTracking,
    );
    function sendTracking(pageView: TileListPageLayout, historic?: boolean): void {
        const browserPage = readBrowserPage();
        const { experimentIds } = collectFeatureTracking(pageView, session.onex);
        const pageViewTracking = getPageViewTracking(!!historic, experimentIds, browserPage.pageCluster);
        trackingService.trackPageView(pageView, pageViewTracking);
        trackFirebase(firebaseTracking, pageView.tileListItems);
        void trackAdjustForTileList(
            pageView.tileListItems,
            browserPage.avContentPageType,
            browserPage.windowLocationUrl,
            session.onex,
            appsTracking,
        );
    }
    return {
        session,
        eventsIncoming,
        eventsOutgoing,
        trackingService,
        variationLoadingService,
        controller,
        createNavigationCheckpoint: checkpointFactory,
        restoreScrollPosition: restoreBySelector,
        saveScrollSelector,
        savePageToCache: saveToCache,
        maybeRestoreFromCache,
        sendTracking,
        tileListLoaded: tileListLoadedNexus.events,
        cleanup() {
            disposeEntryRuleTracking();
            tileListLoadedNexus.cleanup();
        },
    };
}
